import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, RefreshCw, AlertTriangle, MapPin, Target as TargetIco, Megaphone, Radio as RadioIco, X, Wand2, Upload, Send, MessageSquare, Trash2, Clock, CheckCircle2, Eye, Volume2, Video, Download, Mic, Image as ImageIcon, Film, Recycle } from "lucide-react";
import { sr, auth, MEDIA_CHANNELS } from "../../lib/sr";
import ChannelPicker from "../../components/smartreach/ChannelPicker";
import AudienceBrowser from "../../components/smartreach/AudienceBrowser";
import MusicControl from "../../components/smartreach/MusicControl";
import LuxuryPlaybook from "./growthplan/LuxuryPlaybook";
import EventTargeting from "../../components/smartreach/EventTargeting";
import LifeSciencesTargeting from "../../components/smartreach/LifeSciencesTargeting";
import StrategyTracker from "../../components/smartreach/StrategyTracker";
import BonusInventory from "../../components/smartreach/BonusInventory";

const AUDIENCE_CATALOG_INDUSTRIES = {
  "doctors": "Medical & Dental", "medical-professionals": "Medical Professionals",
  "real-estate": "Real Estate", "commercial-real-estate": "Commercial Real Estate",
  "apartments": "Apartments & Multifamily", "property-management": "Property Management",
  "restaurants": "Restaurants & QSR", "retail": "Retail & Grocery", "auto": "Auto Dealerships",
  "home-services": "Home Services", "gyms-fitness": "Gyms & Fitness",
  "professional-services": "Professional Services",
  "luxury": "Luxury Products",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const STAFF_ROLES = ["admin", "super_admin", "campaign_manager", "compliance_reviewer", "salesperson"];
const APPROVER_ROLES = ["admin", "super_admin", "campaign_manager"];
const REVIEW_META = {
  approved: { label: "Approved · ready to launch", cls: "sr-badge-green", Icon: CheckCircle2 },
  pending_review: { label: "Awaiting client review", cls: "sr-badge-amber", Icon: Clock },
  changes_requested: { label: "Changes requested", cls: "sr-badge-amber", Icon: MessageSquare },
  draft: { label: "Draft · not sent", cls: "sr-badge-gray", Icon: Wand2 },
};

const CONF = { LOW: "sr-badge-amber", MEDIUM: "sr-badge", HIGH: "sr-badge-green" };

function Uplift({ pct }) {
  if (pct == null) return <span className="sr-muted">—</span>;
  const up = pct >= 0;
  return <strong style={{ color: up ? "var(--success)" : "#B42318" }}>{up ? "+" : ""}{pct}%</strong>;
}

function UpliftBars({ daily }) {
  if (!daily || daily.length === 0) return null;
  const max = Math.max(1, ...daily.map((d) => d.views || 0));
  return (
    <div style={{ marginTop: 8 }} data-testid="plan-uplift-chart">
      <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 44 }}>
        {daily.map((d, i) => (
          <div key={i} title={`${d.date}: ${d.views} views`} style={{
            flex: 1, minWidth: 2, borderRadius: 2,
            background: d.phase === "after" ? "var(--success)" : "var(--border)",
            height: `${Math.max(4, ((d.views || 0) / max) * 100)}%` }} />
        ))}
      </div>
      <div className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>Grey = before launch · green = after</div>
    </div>
  );
}

function PlanUpliftCard({ planId }) {
  const [data, setData] = useState(null);
  useEffect(() => { sr.planUplift(planId).then(setData).catch(() => setData({ launched: false })); }, [planId]);
  if (!data || !data.launched) return null;
  const px = data.pixel;
  const ga = data.ga;
  const hasPixel = px && px.available;
  return (
    <div className="sr-card" style={{ marginTop: 14, borderLeft: "4px solid var(--success)" }} data-testid="plan-uplift-card">
      <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Campaign uplift · since launch {data.launch_date}</div>
      {!hasPixel && !ga && (
        <p className="sr-muted" style={{ fontSize: 13 }} data-testid="plan-uplift-empty">We'll show before/after results here as your website pixel collects data (and Google Analytics once connected).</p>
      )}
      {hasPixel && (
        <div style={{ marginBottom: ga ? 12 : 0 }} data-testid="plan-uplift-pixel">
          <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 700, marginBottom: 4 }}>From your website pixel · {px.window_days}-day windows</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 14 }}>
            <span>Page views <Uplift pct={px.uplift?.page_views} /> <span className="sr-muted">({px.before?.page_views}→{px.after?.page_views})</span></span>
            <span>Conversions <Uplift pct={px.uplift?.conversions} /> <span className="sr-muted">({px.before?.conversions}→{px.after?.conversions})</span></span>
            {px.has_conversion_value && <span data-testid="plan-uplift-revenue">Revenue <Uplift pct={px.uplift?.revenue} /> <span className="sr-muted">(${Number(px.before?.revenue || 0).toLocaleString()}→${Number(px.after?.revenue || 0).toLocaleString()})</span></span>}
          </div>
          <UpliftBars daily={px.daily} />
          {(!px.conversion_paths || px.conversion_paths.length === 0) && (
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>Tip: mark your conversion pages in Setup to track sales/leads, not just views.</p>
          )}
        </div>
      )}
      {ga && (
        <div data-testid="plan-uplift-ga">
          <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 700, marginBottom: 4 }}>From Google Analytics · {ga.window_days}-day windows</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 14 }}>
            <span>Sessions <Uplift pct={ga.uplift?.sessions} /> <span className="sr-muted">({ga.before?.sessions}→{ga.after?.sessions})</span></span>
            <span>Users <Uplift pct={ga.uplift?.users} /></span>
            <span>Conversions <Uplift pct={ga.uplift?.conversions} /></span>
          </div>
        </div>
      )}
    </div>
  );
}

function ModeledResult({ mr }) {
  if (!mr) return null;
  return (
    <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }} data-testid="playbook-modeled">
      <strong style={{ color: "var(--navy)" }}>{mr.low}–{mr.high} {mr.metric}{mr.unit}</strong> modeled for {mr.sample} at ${Number(mr.at_budget).toLocaleString()}/mo
    </p>
  );
}

function PlaybookPicker({ onApplied }) {
  const [pbs, setPbs] = useState(null);
  const [cat, setCat] = useState(null);
  const [busy, setBusy] = useState("");
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  useEffect(() => { sr.playbooks().then((r) => { setPbs(r.playbooks || []); setCat(r.my_category || null); }).catch(() => setPbs([])); }, []);
  const apply = async (key) => { setBusy(key); try { await sr.applyPlaybook(key); toast.success("Playbook applied — draft plan created"); onApplied && onApplied(); } catch { toast.error("Failed"); } finally { setBusy(""); } };
  const toggleCompare = (key) => setCompare((c) => (c.includes(key) ? c.filter((x) => x !== key) : c.length >= 2 ? [c[1], key] : [...c, key]));
  if (!pbs || pbs.length === 0) return null;
  const rec = pbs.find((p) => p.recommended);
  const rest = pbs.filter((p) => !p.recommended);
  const cmp = compare.map((k) => pbs.find((p) => p.key === k)).filter(Boolean);
  const CompareCheck = ({ p }) => (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--muted)", cursor: "pointer", marginTop: 8 }}>
      <input type="checkbox" checked={compare.includes(p.key)} onChange={() => toggleCompare(p.key)} data-testid={`playbook-compare-${p.key}`} /> Compare
    </label>
  );
  return (
    <div style={{ marginTop: 24 }}>
      <h2 style={{ fontSize: 18 }}>Start from an industry playbook</h2>
      <p className="sr-muted" style={{ fontSize: 14, marginTop: 4 }}>Proven templates — offer, channel mix and budget preset for your vertical, with the modeled result for similar businesses. Tick two to compare. Edit anything after.</p>

      {rec && (
        <div className="sr-card" style={{ marginTop: 14, border: "1px solid var(--gold)", background: "var(--gold-light, #FDF9EC)" }} data-testid="playbook-recommended">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <span className="sr-badge sr-badge-gold" data-testid="playbook-rec-label"><Sparkles size={11} /> Best fit for {cat || rec.industry}</span>
              <h3 style={{ fontSize: 19, marginTop: 8 }}>{rec.name}</h3>
              <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{rec.offer}</p>
              <ModeledResult mr={rec.modeled_results} />
              {rec.modeled_results?.basis && <p className="sr-muted" style={{ fontSize: 11, marginTop: 3, fontStyle: "italic" }}>{rec.modeled_results.basis} — a modeled estimate, not a guarantee.</p>}
              <CompareCheck p={rec} />
            </div>
            <button className="sr-btn sr-btn-gold" onClick={() => apply(rec.key)} disabled={!!busy} data-testid={`playbook-apply-${rec.key}`}>
              {busy === rec.key ? <Loader2 size={14} className="sr-spin" /> : "Use this playbook"}
            </button>
          </div>
        </div>
      )}

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", marginTop: 14 }}>
        {rest.map((p) => (
          <div key={p.key} className="sr-card sr-card-hover" data-testid={`playbook-${p.key}`}>
            <span className="sr-badge sr-badge-gray">{p.industry}</span>
            <h3 style={{ fontSize: 17, marginTop: 8 }}>{p.name}</h3>
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{p.offer}</p>
            <ModeledResult mr={p.modeled_results} />
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>Goal: {p.goal} · ${p.suggested_budget.toLocaleString()}/mo</p>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <CompareCheck p={p} />
              <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 8 }} onClick={() => apply(p.key)} disabled={!!busy} data-testid={`playbook-apply-${p.key}`}>
                {busy === p.key ? <Loader2 size={14} className="sr-spin" /> : "Use this playbook"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {compare.length > 0 && (
        <div style={{ position: "sticky", bottom: 12, marginTop: 14, display: "flex", justifyContent: "center", zIndex: 20 }}>
          <div className="sr-card" style={{ display: "flex", alignItems: "center", gap: 12, boxShadow: "0 6px 24px rgba(16,24,40,.18)" }}>
            <span style={{ fontSize: 13, color: "var(--navy)" }}>{compare.length} of 2 selected to compare</span>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setCompare([])} data-testid="playbook-compare-clear">Clear</button>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setShowCompare(true)} disabled={compare.length < 2} data-testid="playbook-compare-open">Compare</button>
          </div>
        </div>
      )}

      {showCompare && cmp.length === 2 && (
        <div data-testid="playbook-compare-modal" onClick={() => setShowCompare(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(16,24,40,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h2 style={{ fontSize: 20, color: "var(--navy)", margin: 0 }}>Compare playbooks</h2>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setShowCompare(false)} data-testid="playbook-compare-close"><X size={16} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {cmp.map((p) => (
                <div key={p.key} className="sr-card" style={{ background: "var(--surface, #fff)" }} data-testid={`playbook-compare-col-${p.key}`}>
                  <span className={`sr-badge ${p.recommended ? "sr-badge-gold" : "sr-badge-gray"}`}>{p.recommended ? "Best fit" : p.industry}</span>
                  <h3 style={{ fontSize: 17, marginTop: 8 }}>{p.name}</h3>
                  <div className="sr-eyebrow" style={{ marginTop: 10 }}>Offer</div>
                  <p className="sr-muted" style={{ fontSize: 13 }}>{p.offer}</p>
                  <div className="sr-eyebrow" style={{ marginTop: 10 }}>Modeled result</div>
                  <ModeledResult mr={p.modeled_results} />
                  <div className="sr-eyebrow" style={{ marginTop: 10 }}>Channel mix</div>
                  <div style={{ marginTop: 4 }}>
                    {(p.channel_mix || []).map((c) => (
                      <div key={c.channel} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}>
                        <span style={{ color: "var(--navy)" }}>{c.channel}</span><span className="sr-muted">{c.budget_pct}%</span>
                      </div>
                    ))}
                  </div>
                  <p className="sr-muted" style={{ fontSize: 12, marginTop: 10 }}>Goal: {p.goal} · ${p.suggested_budget.toLocaleString()}/mo</p>
                  <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={() => { setShowCompare(false); apply(p.key); }} disabled={!!busy} data-testid={`playbook-compare-use-${p.key}`}>Use this playbook</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Block = ({ title, children }) => (
  <div className="sr-card" style={{ marginTop: 14 }}>
    <div className="sr-eyebrow" style={{ marginBottom: 8 }}>{title}</div>
    {children}
  </div>
);

const usdShort = (n) => `$${Math.round(n || 0).toLocaleString()}`;
const chLabel = (k) => (k === "radio" ? "Radio · FunAsia" : k.charAt(0).toUpperCase() + k.slice(1));
const CH_BADGE_LABEL = Object.fromEntries(MEDIA_CHANNELS.map((c) => [c.key, c.label]));
const chKeyFromName = (n) => {
  const s = (n || "").toLowerCase();
  if (s.includes("chatgpt") || s.includes("chat gpt")) return "chatgpt";
  if (s.includes("radio")) return "radio";
  if (s.includes("search") || s.includes("google")) return "google";
  if (s.includes("meta") || s.includes("social") || s.includes("facebook") || s.includes("instagram")) return "social";
  if (s.includes("native")) return "native";
  if (s.includes("ctv") || s.includes("ott")) return "ctv";
  if (s.includes("video")) return "video";
  if (s.includes("audio")) return "audio";
  if (s.includes("display")) return "display";
  return null;
};

const VIDEO_CHANNELS = ["video", "ctv", "audio"];
const isVideoCh = (k) => VIDEO_CHANNELS.includes(k);
const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return " · " + new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

function SceneList({ scenes, audio }) {
  if (!scenes || !scenes.length) return null;
  return (
    <div style={{ marginTop: 8, display: "grid", gap: 6 }} data-testid="script-scenes">
      {scenes.map((s, i) => (
        <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5 }}>
          <span style={{ flexShrink: 0, width: 20, height: 20, borderRadius: 999, background: "var(--navy)", color: "#fff", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{i + 1}</span>
          <div>
            {!audio && s.visual && <div style={{ color: "var(--muted)" }}><em>{s.visual}</em></div>}
            {s.voiceover && <div style={{ color: "var(--navy)" }}>🔊 {s.voiceover}</div>}
            {s.sfx && <div style={{ color: "var(--muted)" }}>♪ {s.sfx}</div>}
            {s.on_screen && <div className="sr-badge sr-badge-gray" style={{ marginTop: 3 }}>{s.on_screen}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

function VariantPicker({ planId, channelKey, channelLabel, onClose, onPicked }) {
  const video = isVideoCh(channelKey);
  const audio = channelKey === "audio";
  const [concepts, setConcepts] = useState(null);
  const [format, setFormat] = useState("copy");
  const [duration, setDuration] = useState(15);
  const [aiWarn, setAiWarn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(-1);
  const fetchVariants = async (dur = duration) => {
    setBusy(true); setConcepts(null);
    try {
      const r = await sr.creativeVariantsFor(planId, channelKey, 3, dur);
      setConcepts(r.concepts || []); setFormat(r.format || "copy"); setAiWarn(r.ai_available === false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate options"); onClose(); }
    finally { setBusy(false); }
  };
  useEffect(() => { fetchVariants(); /* eslint-disable-next-line */ }, [channelKey]);
  const changeDuration = (d) => { setDuration(d); fetchVariants(d); };
  const pick = async (c, i) => {
    setSaving(i);
    try {
      const payload = format === "script"
        ? { source: "ai", channel: channelKey, format: "script", title: c.title, script: c.scenes, music: c.music, cta: typeof c.cta === "string" ? c.cta : undefined, duration_sec: c.duration_sec || duration }
        : { source: "ai", channel: channelKey, headline: c.headline, body: c.body, cta: c.cta };
      const r = await sr.addPlanCreative(planId, payload);
      onPicked(r.growth_plan);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(-1); }
  };
  return (
    <div data-testid="creative-variant-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(16,24,40,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: video ? 820 : 720, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 19, color: "var(--navy)", margin: 0 }}>Choose a {video ? "script" : "creative"} for {channelLabel}</h2>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onClose} data-testid="creative-variant-close"><X size={16} /></button>
        </div>
        <p className="sr-muted" style={{ fontSize: 13, marginBottom: 12 }}>
          {video ? "Pick a scene-and-voiceover script. Choose the length and generate a fresh set anytime." : "Pick the option you like best, or generate a fresh set."}
        </p>
        {video && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }} data-testid="script-duration-toggle">
            {[15, 30].map((d) => (
              <button key={d} className={`sr-btn sr-btn-sm ${duration === d ? "sr-btn-primary" : "sr-btn-ghost"}`}
                onClick={() => changeDuration(d)} disabled={busy} data-testid={`script-duration-${d}`}>{d}s</button>
            ))}
          </div>
        )}
        {aiWarn && <p className="sr-badge sr-badge-amber" style={{ marginBottom: 10 }}><AlertTriangle size={12} /> AI was busy — these are safe templates you can edit.</p>}
        {busy || !concepts ? (
          <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        ) : format === "script" ? (
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))" }}>
            {concepts.map((c, i) => (
              <div key={i} className="sr-card sr-card-hover" data-testid={`creative-variant-${i}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {c.angle && <span className="sr-badge sr-badge-gray">{c.angle}</span>}
                  <span className="sr-badge sr-badge-gold">{c.duration_sec || duration}s</span>
                </div>
                <strong style={{ color: "var(--navy)", display: "block", marginTop: 6 }}>{c.title}</strong>
                <SceneList scenes={c.scenes} audio={audio} />
                {c.music && <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>♫ {c.music}</p>}
                <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={() => pick(c, i)} disabled={saving !== -1} data-testid={`creative-variant-use-${i}`}>
                  {saving === i ? <Loader2 size={14} className="sr-spin" /> : <><Check size={14} /> Use this script</>}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
            {concepts.map((c, i) => (
              <div key={i} className="sr-card sr-card-hover" data-testid={`creative-variant-${i}`}>
                {c.angle && <span className="sr-badge sr-badge-gray" style={{ marginBottom: 6 }}>{c.angle}</span>}
                <strong style={{ color: "var(--navy)", display: "block" }}>{c.headline}</strong>
                <p className="sr-muted" style={{ fontSize: 13, marginTop: 6 }}>{c.body}</p>
                {c.cta && <div style={{ marginTop: 8 }}><span className="sr-badge sr-badge-gold">{c.cta}</span></div>}
                <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10, width: "100%" }} onClick={() => pick(c, i)} disabled={saving !== -1} data-testid={`creative-variant-use-${i}`}>
                  {saving === i ? <Loader2 size={14} className="sr-spin" /> : <><Check size={14} /> Use this</>}
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => fetchVariants()} disabled={busy} data-testid="creative-variant-regen"><RefreshCw size={14} /> Generate new options</button>
        </div>
      </div>
    </div>
  );
}

// ---- Realistic ad mockups so clients see exactly what runs ----
function CreativePreview({ channelKey, creative, bizName, planId, canDownload }) {
  const c = creative || {};
  const name = bizName || "Your business";
  const [downloading, setDownloading] = useState(false);
  const doDownload = async () => {
    setDownloading(true);
    try {
      const blob = await sr.downloadPlanVideo(planId, channelKey);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const slug = (name || "smartreach").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "smartreach";
      a.href = url; a.download = `${slug}-${channelKey}-ad.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Download unlocks once your campaign is live"); }
    finally { setDownloading(false); }
  };
  const initial = (name.trim()[0] || "B").toUpperCase();
  const img = c.image_url ? (c.image_url.startsWith("/") ? BACKEND + c.image_url : c.image_url) : null;
  const gradient = "linear-gradient(135deg, var(--navy), var(--blue))";
  const scenes = c.script || [];
  const firstOnScreen = (scenes.find((s) => s.on_screen) || {}).on_screen;

  if (channelKey === "social") {
    return (
      <div data-testid="preview-social" style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 999, background: gradient, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{initial}</div>
          <div><div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>{name}</div><div className="sr-muted" style={{ fontSize: 11 }}>Sponsored · Meta</div></div>
        </div>
        {c.body && <p style={{ fontSize: 13, padding: "0 10px 8px", color: "var(--navy)" }}>{c.body}</p>}
        <div style={{ height: 150, background: img ? `center/cover no-repeat url(${img})` : gradient }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 10, background: "#F5F7FA" }}>
          <strong style={{ fontSize: 13, color: "var(--navy)" }}>{c.headline}</strong>
          {c.cta && <span className="sr-btn sr-btn-primary sr-btn-sm" style={{ pointerEvents: "none" }}>{c.cta}</span>}
        </div>
      </div>
    );
  }
  if (channelKey === "google") {
    return (
      <div data-testid="preview-google" style={{ border: "1px solid var(--border)", borderRadius: 12, background: "#fff", padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, border: "1px solid #999", borderRadius: 4, padding: "0 4px" }}>Ad</span>
          <span style={{ fontSize: 12, color: "#202124" }}>{name.toLowerCase().replace(/[^a-z0-9]+/g, "")}.com</span>
        </div>
        <div style={{ color: "#1a0dab", fontSize: 18, lineHeight: 1.2 }}>{c.headline}</div>
        <div style={{ color: "#4d5156", fontSize: 13, marginTop: 4 }}>{c.body}</div>
        {c.cta && <div style={{ color: "#1a0dab", fontSize: 13, marginTop: 6 }}>{c.cta} ›</div>}
      </div>
    );
  }
  if (channelKey === "display" || channelKey === "native") {
    const native = channelKey === "native";
    return (
      <div data-testid={`preview-${channelKey}`} style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "#fff" }}>
        <div style={{ position: "relative", height: 140, background: img ? `center/cover no-repeat url(${img})` : gradient }}>
          {!native && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "center", padding: 14, background: "rgba(11,20,38,.35)" }}>
              <strong style={{ color: "#fff", fontSize: 17 }}>{c.headline}</strong>
              {c.cta && <span style={{ marginTop: 8, alignSelf: "flex-start", background: "var(--gold)", color: "var(--navy)", fontWeight: 700, fontSize: 12, padding: "6px 12px", borderRadius: 999 }}>{c.cta}</span>}
            </div>
          )}
        </div>
        {native && (
          <div style={{ padding: 10 }}>
            <div className="sr-muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>Sponsored · {name}</div>
            <strong style={{ fontSize: 14, color: "var(--navy)", display: "block", marginTop: 3 }}>{c.headline}</strong>
            {c.body && <p className="sr-muted" style={{ fontSize: 12, marginTop: 3 }}>{c.body}</p>}
          </div>
        )}
      </div>
    );
  }
  if (channelKey === "audio") {
    return (
      <div data-testid="preview-audio" style={{ border: "1px solid var(--border)", borderRadius: 12, background: "var(--navy)", color: "#fff", padding: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <div style={{ width: 56, height: 56, borderRadius: 8, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20 }}>{initial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="sr-muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 1, color: "#9fb3c8" }}>Now playing · Ad · {c.duration_sec || 15}s</div>
          <strong style={{ fontSize: 14, display: "block" }}>{c.headline || `${name} spot`}</strong>
          <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 18, marginTop: 6 }}>
            {[6, 12, 8, 16, 10, 14, 7, 13, 9, 15, 6, 11].map((h, i) => <span key={i} style={{ width: 3, height: h, background: "var(--gold)", borderRadius: 2 }} />)}
          </div>
          {scenes[0]?.voiceover && <p style={{ fontSize: 12, marginTop: 6, color: "#cdd8e3" }}>🔊 {scenes[0].voiceover}</p>}
        </div>
        {c.voiceover_url && <audio controls src={BACKEND + c.voiceover_url} data-testid="preview-audio-player" style={{ width: 150, height: 32 }} />}
      </div>
    );
  }
  // video / ctv — one combined ad video (scene montage with the presenter composited in)
  const videoSrc = c.video_url ? (c.video_url.startsWith("/") ? BACKEND + c.video_url : c.video_url) : null;
  const montageSrc = c.montage_url ? (c.montage_url.startsWith("/") ? BACKEND + c.montage_url : c.montage_url) : null;
  const rendering = c.video_status === "processing";
  const vertical = c.video_aspect === "9:16";
  const ar = (c.video_aspect || "16:9").replace(":", "/");
  const sceneImgs = c.scene_images || [];
  const mainSrc = montageSrc || videoSrc;
  const isCombined = !!montageSrc && c.montage_combined;
  return (
    <div data-testid={`preview-${channelKey}`}>
      {mainSrc ? (
        <>
          <video controls playsInline src={mainSrc} data-testid={montageSrc ? `preview-montage-player-${channelKey}` : `preview-video-player-${channelKey}`}
            style={{ width: vertical ? "auto" : "100%", maxWidth: "100%", maxHeight: vertical ? 460 : "none", margin: vertical ? "0 auto" : 0, borderRadius: 12, background: "#0B1426", aspectRatio: ar, display: "block" }} />
          <div className="sr-muted" style={{ fontSize: 11, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }} data-testid={`preview-video-caption-${channelKey}`}>
            {montageSrc ? (isCombined ? <><Film size={12} /> Combined ad — scene montage with your presenter</> : <><Film size={12} /> Scene montage synced to the voiceover</>) : <><Video size={12} /> Presenter video</>}
          </div>
        </>
      ) : (
        <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", background: "#0B1426", aspectRatio: ar, maxHeight: vertical ? 460 : "none", margin: vertical ? "0 auto" : 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {rendering ? (
            <div style={{ color: "#fff", textAlign: "center" }} data-testid={`preview-video-rendering-${channelKey}`}>
              <Loader2 className="sr-spin" size={26} />
              <div style={{ fontSize: 12, marginTop: 8, color: "#cdd8e3" }}>Rendering your video… (1–3 min)</div>
            </div>
          ) : (
            <>
              <div style={{ width: 46, height: 46, borderRadius: 999, background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <span style={{ borderLeft: "14px solid var(--navy)", borderTop: "9px solid transparent", borderBottom: "9px solid transparent", marginLeft: 4 }} />
              </div>
              {firstOnScreen && <div style={{ position: "absolute", left: 12, bottom: 12, background: "rgba(0,0,0,.55)", color: "#fff", fontSize: 12, padding: "4px 8px", borderRadius: 6 }}>{firstOnScreen}</div>}
            </>
          )}
          <span style={{ position: "absolute", right: 10, top: 10, background: "var(--gold)", color: "var(--navy)", fontWeight: 700, fontSize: 11, padding: "2px 8px", borderRadius: 999 }}>{channelKey === "ctv" ? "CTV / OTT" : "Video"} · {c.duration_sec || 15}s</span>
        </div>
      )}
      {videoSrc && (
        canDownload ? (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={doDownload} disabled={downloading} data-testid={`video-download-${channelKey}`} style={{ marginTop: 8 }}>
            {downloading ? <Loader2 size={13} className="sr-spin" /> : <Download size={13} />} Download video
          </button>
        ) : (
          <div data-testid={`video-download-locked-${channelKey}`} className="sr-muted" style={{ marginTop: 8, fontSize: 11.5, display: "flex", alignItems: "center", gap: 6 }}>
            <Download size={12} /> Download unlocks once your campaign is live with us.
          </div>
        )
      )}
      {c.video_status === "failed" && <p style={{ fontSize: 12, color: "#B42318", marginTop: 6 }} data-testid={`preview-video-failed-${channelKey}`}>Video rendering failed{c.video_error ? ` — ${c.video_error}` : ""}. Please try again.</p>}
      {c.montage_status === "failed" && <p style={{ fontSize: 12, color: "#B42318", marginTop: 6 }} data-testid={`preview-montage-failed-${channelKey}`}>Montage build failed — please retry.</p>}
      {!mainSrc && !rendering && <p className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>Storyboard concept — render a presenter and press “Create combined video”.</p>}

      {/* Storyboard gallery */}
      {sceneImgs.length > 0 && (
        <div style={{ marginTop: 12 }} data-testid={`scene-gallery-${channelKey}`}>
          <div className="sr-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Storyboard · {sceneImgs.length} scenes</div>
          <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
            {sceneImgs.map((s) => (
              <div key={s.idx} data-testid={`scene-thumb-${channelKey}-${s.idx}`} style={{ flex: "0 0 auto", width: 128 }}>
                <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", aspectRatio: "16/9", background: "#0B1426" }}>
                  <img src={s.url?.startsWith("/") ? BACKEND + s.url : s.url} alt={`Scene ${s.idx + 1}`} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <span style={{ position: "absolute", left: 6, top: 6, background: "rgba(0,0,0,.6)", color: "#fff", fontSize: 10, padding: "1px 6px", borderRadius: 999 }}>{s.idx + 1}</span>
                </div>
                {s.on_screen && <div className="sr-muted" style={{ fontSize: 10, marginTop: 3, lineHeight: 1.3 }}>{s.on_screen}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {!mainSrc && c.voiceover_url && <audio controls src={BACKEND + c.voiceover_url} data-testid="preview-audio-player" style={{ width: "100%", height: 34, marginTop: 8 }} />}
      <SceneList scenes={scenes} audio={false} />
    </div>
  );
}

function VoicePicker({ planId, channelKey, onClose, onDone }) {
  const [voices, setVoices] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    sr.ttsVoices().then((r) => setVoices(r.voices || [])).catch(() => { toast.error("Could not load voices"); onClose(); });
  }, []);
  const use = async (voiceId) => {
    setBusy(voiceId);
    try { const r = await sr.generateVoiceover(planId, channelKey, voiceId); onDone(r.growth_plan); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate voiceover"); setBusy(""); }
  };
  return (
    <div data-testid="voice-picker-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(16,24,40,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 19, color: "var(--navy)", margin: 0 }}>Choose a voice</h2>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onClose} data-testid="voice-picker-close"><X size={16} /></button>
        </div>
        <p className="sr-muted" style={{ fontSize: 13, marginBottom: 12 }}>Press play to preview, then pick the voice to read your script.</p>
        {!voices ? (
          <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {voices.map((v) => (
              <div key={v.id} className="sr-card" style={{ display: "flex", alignItems: "center", gap: 12, padding: 12 }} data-testid={`voice-opt-${v.id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ color: "var(--navy)" }}>{v.label}</strong>
                  <div className="sr-muted" style={{ fontSize: 12 }}>{v.desc}</div>
                </div>
                <audio controls preload="none" src={BACKEND + v.sample_url} data-testid={`voice-sample-${v.id}`} style={{ height: 32, maxWidth: 160 }} />
                <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => use(v.id)} disabled={!!busy} data-testid={`voice-use-${v.id}`}>
                  {busy === v.id ? <Loader2 size={14} className="sr-spin" /> : <><Volume2 size={14} /> Use</>}
                </button>
              </div>
            ))}
          </div>
        )}
        {busy && <p className="sr-muted" style={{ fontSize: 12, marginTop: 10, textAlign: "center" }}><Loader2 size={12} className="sr-spin" /> Recording your voiceover…</p>}
      </div>
    </div>
  );
}

function VideoPicker({ planId, channelKey, channelLabel, onClose, onStarted }) {
  const [opts, setOpts] = useState(null);
  const [presenters, setPresenters] = useState([]);
  const [avatar, setAvatar] = useState(null);   // stock avatar_id
  const [photo, setPhoto] = useState(null);      // uploaded talking_photo_id
  const [voice, setVoice] = useState(null);
  const [aspect, setAspect] = useState("16:9");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [pendingClone, setPendingClone] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  useEffect(() => {
    sr.videoOptions()
      .then((o) => { setOpts(o); setAvatar(o.avatars?.[0]?.avatar_id || null); setVoice(o.voices?.[0]?.voice_id || null); })
      .catch(() => setErr("Video studio is temporarily unavailable — please try again shortly."));
    sr.listPresenters().then((r) => setPresenters(r.presenters || [])).catch(() => {});
  }, []);
  const pickAvatar = (id) => { setAvatar(id); setPhoto(null); };
  const pickPhoto = (tp) => { setPhoto(tp); setAvatar(null); };
  const onCloneAudio = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setPendingClone({ file: f, name: "My voice" });
    if (audioRef.current) audioRef.current.value = "";
  };
  const doClone = async () => {
    if (!pendingClone?.file) return;
    const nm = (pendingClone.name || "").trim() || "My voice";
    setCloning(true);
    try {
      const r = await sr.cloneVoice(nm, pendingClone.file);
      const o = await sr.videoOptions();
      setOpts(o);
      setVoice(r.voice_id);
      setPendingClone(null);
      toast.success("Your voice is ready — it's selected below");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not clone your voice");
    } finally {
      setCloning(false);
    }
  };
  const removeClone = async (vid) => {
    try {
      await sr.deleteVoiceClone(vid);
      const o = await sr.videoOptions();
      setOpts(o);
      if (voice === vid) setVoice(o.voices?.[0]?.voice_id || null);
      toast.success("Voice removed");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not remove that voice"); }
  };
  const onUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const r = await sr.uploadVideoPhoto(f);
      const p = r.presenter || { id: r.talking_photo_id, talking_photo_id: r.talking_photo_id, preview_url: r.preview_url };
      setPresenters((prev) => [p, ...prev]);
      pickPhoto(r.talking_photo_id);
      toast.success("Photo added as your presenter");
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Could not upload photo"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const start = async () => {
    if (!avatar && !photo) { toast.error("Pick a presenter or upload your photo/logo"); return; }
    if (!voice) { toast.error("Pick a voice"); return; }
    setBusy(true);
    try {
      const r = await sr.generatePlanVideo(planId, channelKey, { avatar_id: photo ? null : avatar, talking_photo_id: photo || null, voice_id: voice, aspect });
      onStarted(r.growth_plan);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start video rendering"); setBusy(false); }
  };
  const ASPECTS = [{ id: "16:9", hint: "Web / CTV" }, { id: "9:16", hint: "Reels / TikTok" }, { id: "1:1", hint: "Feed · square" }];
  return (
    <div data-testid="video-picker-modal" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(16,24,40,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 620, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 19, color: "var(--navy)", margin: 0 }}>Generate {channelLabel} ad</h2>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onClose} data-testid="video-picker-close"><X size={16} /></button>
        </div>
        <p className="sr-muted" style={{ fontSize: 13, marginBottom: 12 }}>Pick a presenter — or upload your own photo/logo — plus a voice and size. We'll render a real MP4 from your script (1–3 min).</p>
        {err ? (
          <div className="sr-empty" style={{ color: "#B42318" }} data-testid="video-picker-error">{err}</div>
        ) : !opts ? (
          <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        ) : (
          <>
            <div className="sr-label" style={{ marginBottom: 6 }}>Presenter</div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onUpload} style={{ display: "none" }} data-testid="video-presenter-file" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(96px,1fr))", gap: 8, marginBottom: 14 }}>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="video-presenter-upload"
                style={{ border: "1px dashed var(--gold)", borderRadius: 10, background: "#FFFBEB", cursor: "pointer", height: 108, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, color: "var(--navy)", padding: 6 }}>
                {uploading ? <Loader2 size={18} className="sr-spin" /> : <Upload size={18} />}
                <span style={{ fontSize: 10.5, textAlign: "center", lineHeight: 1.2 }}>{uploading ? "Uploading…" : "Upload photo / logo"}</span>
              </button>
              {presenters.map((p) => (
                <button key={p.talking_photo_id} type="button" onClick={() => pickPhoto(p.talking_photo_id)} data-testid={`video-presenter-${p.talking_photo_id}`}
                  style={{ border: photo === p.talking_photo_id ? "2px solid var(--gold)" : "1px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "#fff", cursor: "pointer", padding: 0 }}>
                  <div style={{ height: 84, background: p.preview_url ? `center/cover no-repeat url(${p.preview_url})` : "var(--navy)" }} />
                  <div style={{ fontSize: 10.5, padding: "4px 4px 6px", color: "var(--navy)", textAlign: "center" }}>Your upload</div>
                </button>
              ))}
              {opts.avatars.map((a) => (
                <button key={a.avatar_id} type="button" onClick={() => pickAvatar(a.avatar_id)} data-testid={`video-avatar-${a.avatar_id}`}
                  style={{ border: avatar === a.avatar_id ? "2px solid var(--gold)" : "1px solid var(--border)", borderRadius: 10, overflow: "hidden", background: "#fff", cursor: "pointer", padding: 0 }}>
                  <div style={{ height: 84, background: a.preview ? `center/cover no-repeat url(${a.preview})` : "var(--navy)" }} />
                  <div style={{ fontSize: 10.5, padding: "4px 4px 6px", color: "var(--navy)", textAlign: "center" }}>{a.name}</div>
                </button>
              ))}
            </div>
            <div className="sr-label" style={{ marginBottom: 6 }}>Video size</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
              {ASPECTS.map((a) => (
                <button key={a.id} type="button" onClick={() => setAspect(a.id)} data-testid={`video-aspect-${a.id.replace(":", "x")}`}
                  style={{ flex: 1, minWidth: 120, border: aspect === a.id ? "2px solid var(--gold)" : "1px solid var(--border)", borderRadius: 10, background: aspect === a.id ? "#FFFBEB" : "#fff", cursor: "pointer", padding: "9px 10px", textAlign: "left" }}>
                  <strong style={{ color: "var(--navy)", fontSize: 13 }}>{a.id}</strong>
                  <div className="sr-muted" style={{ fontSize: 11 }}>{a.hint}</div>
                </button>
              ))}
            </div>
            <div className="sr-label" style={{ marginBottom: 6 }}>Voice</div>
            <input ref={audioRef} type="file" accept="audio/*" onChange={onCloneAudio} style={{ display: "none" }} data-testid="video-voice-clone-file" />
            <button type="button" onClick={() => audioRef.current?.click()} disabled={cloning || !!pendingClone} data-testid="video-voice-clone-upload"
              style={{ width: "100%", border: "1px dashed var(--gold)", borderRadius: 10, background: "#FFFBEB", cursor: "pointer", padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, color: "var(--navy)", marginBottom: 8, opacity: pendingClone ? 0.6 : 1 }}>
              {cloning ? <Loader2 size={16} className="sr-spin" /> : <Mic size={16} />}
              <span style={{ textAlign: "left", lineHeight: 1.3 }}>
                <strong style={{ fontSize: 12.5 }}>{cloning ? "Cloning your voice…" : "🎙️ Use my own voice"}</strong>
                <span className="sr-muted" style={{ display: "block", fontSize: 11 }}>Upload a genuine 30–60s recording of you speaking clearly. It becomes a selectable presenter voice.</span>
              </span>
            </button>
            {pendingClone && (
              <div className="sr-card" style={{ padding: 10, marginBottom: 8, border: "1px solid var(--gold)", background: "#FFFDF5" }} data-testid="video-voice-clone-namebar">
                <div className="sr-muted" style={{ fontSize: 11, marginBottom: 6 }}>Name this voice, then save it to your library.</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="sr-input" autoFocus value={pendingClone.name} placeholder="e.g. Owner voice" data-testid="video-voice-clone-name"
                    onChange={(e) => setPendingClone((p) => ({ ...p, name: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") doClone(); }} style={{ flex: 1 }} />
                  <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={doClone} disabled={cloning} data-testid="video-voice-clone-save">
                    {cloning ? <Loader2 size={13} className="sr-spin" /> : "Save voice"}
                  </button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setPendingClone(null)} disabled={cloning} data-testid="video-voice-clone-cancel">Cancel</button>
                </div>
              </div>
            )}
            <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
              {opts.voices.map((v) => (
                <div key={v.voice_id} className="sr-card" onClick={() => setVoice(v.voice_id)} data-testid={`video-voice-${v.voice_id}`}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, border: voice === v.voice_id ? "2px solid var(--gold)" : "1px solid var(--border)", cursor: "pointer" }}>
                  <input type="radio" readOnly checked={voice === v.voice_id} style={{ width: 16, height: 16 }} />
                  <strong style={{ color: "var(--navy)", fontSize: 13, flex: 1, minWidth: 0 }}>{v.name}</strong>
                  {v.cloned && <span style={{ fontSize: 10, fontWeight: 700, color: "var(--navy)", background: "#FFF3C4", borderRadius: 999, padding: "2px 8px" }}>Your voice</span>}
                  {v.preview && <audio controls preload="none" src={v.preview} style={{ height: 30, maxWidth: 150 }} onClick={(e) => e.stopPropagation()} />}
                  {v.cloned && (
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`video-voice-delete-${v.voice_id}`} title="Remove this voice"
                      onClick={(e) => { e.stopPropagation(); removeClone(v.voice_id); }}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button className="sr-btn sr-btn-primary" style={{ width: "100%" }} onClick={start} disabled={busy || (!avatar && !photo) || !voice} data-testid="video-picker-generate">
              {busy ? <><Loader2 size={15} className="sr-spin" /> Starting…</> : <><Video size={15} /> Render video</>}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function CreativeStudio({ plan, channelKeys, editable, setPlan, bizName, canDownload }) {
  const me = auth.getUser();
  const isStaff = !!(me && STAFF_ROLES.includes(me.role));
  const isApprover = !!(me && APPROVER_ROLES.includes(me.role));
  const [designCh, setDesignCh] = useState(null);
  const [uploadCh, setUploadCh] = useState(null);
  const [upForm, setUpForm] = useState({ headline: "", body: "", cta: "", image_url: "" });
  const [saveBusy, setSaveBusy] = useState("");
  const [rowBusy, setRowBusy] = useState("");
  const [voicePickerFor, setVoicePickerFor] = useState(null);
  const [videoPickerFor, setVideoPickerFor] = useState(null);
  const [changesFor, setChangesFor] = useState(null);
  const [changeNote, setChangeNote] = useState("");
  const [reviewBusy, setReviewBusy] = useState("");
  const [designAllBusy, setDesignAllBusy] = useState(false);
  const [designAllProg, setDesignAllProg] = useState(null);
  const [mediaBusy, setMediaBusy] = useState("");
  const [musicVol, setMusicVol] = useState({});

  const genScenes = async (k) => {
    setMediaBusy(`scene-${k}`);
    try { const r = await sr.generateSceneImages(plan.id, k); setPlan(r.growth_plan); toast.success("Scene imagery ready"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't generate scene imagery"); }
    finally { setMediaBusy(""); }
  };
  const genMontage = async (k) => {
    setMediaBusy(`montage-${k}`);
    try { const r = await sr.generateMontage(plan.id, k, undefined, musicVol[k]); setPlan(r.growth_plan); toast.success("Combined video ready — press play"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't build the combined video"); }
    finally { setMediaBusy(""); }
  };

  const creatives = plan.creatives || {};
  const keys = Array.from(new Set([...((channelKeys && channelKeys.length) ? channelKeys : []), ...Object.keys(creatives)]));
  const labelFor = (k) => CH_BADGE_LABEL[k] || (creatives[k] && creatives[k].channel_label) || k;
  const pendingKeys = Object.keys(creatives).filter((k) => ["pending_review", "changes_requested"].includes(creatives[k]?.review_status));
  const draftKeys = Object.keys(creatives).filter((k) => ["draft", "changes_requested"].includes(creatives[k]?.review_status));
  const unapprovedKeys = Object.keys(creatives).filter((k) => creatives[k]?.review_status !== "approved");
  const designedCount = Object.keys(creatives).length;
  const undesignedKeys = keys.filter((k) => !creatives[k]);

  // Poll HeyGen for any video/CTV creative that is still rendering, and attach the mp4 when ready.
  const renderingSig = keys.map((k) => `${k}:${creatives[k]?.video_status || ""}:${creatives[k]?.video_url || ""}`).join("|");
  useEffect(() => {
    const processing = keys.filter((k) => creatives[k]?.video_status === "processing");
    if (!processing.length) return;
    let cancelled = false;
    const run = (k) => setTimeout(async function poll() {
      if (cancelled) return;
      let r;
      try { r = await sr.planVideoStatus(plan.id, k); }
      catch { if (!cancelled) setTimeout(poll, 12000); return; }
      if (cancelled) return;
      if (r.status === "completed") { if (r.growth_plan) setPlan(r.growth_plan); toast.success("AI video ready — press play"); return; }
      if (r.status === "failed") { if (r.growth_plan) setPlan(r.growth_plan); toast.error("Video rendering failed — please try again"); return; }
      setTimeout(poll, 12000);
    }, 12000);
    const timers = processing.map(run);
    return () => { cancelled = true; timers.forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderingSig]);

  const openUpload = (k) => { setUploadCh(k); setUpForm({ headline: "", body: "", cta: "", image_url: "" }); };
  const saveUpload = async () => {
    if (!upForm.headline.trim() && !upForm.image_url.trim()) { toast.error("Add a headline or an image URL"); return; }
    setSaveBusy("upload");
    try {
      const r = await sr.addPlanCreative(plan.id, { source: "upload", channel: uploadCh, ...upForm });
      setPlan(r.growth_plan); setUploadCh(null);
      toast.success("Creative added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaveBusy(""); }
  };
  const removeCreative = async (k) => {
    setRowBusy(k);
    try { const r = await sr.deletePlanCreative(plan.id, k); setPlan(r.growth_plan); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
    finally { setRowBusy(""); }
  };
  const sendForReview = async () => {
    setReviewBusy("send");
    try { const r = await sr.sendCreativesForReview(plan.id); setPlan(r.growth_plan); toast.success(`${r.sent} creative(s) sent to the client for review`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send"); }
    finally { setReviewBusy(""); }
  };
  const staffApprove = async (channels) => {
    setReviewBusy(channels && channels.length === 1 ? `staff-approve-${channels[0]}` : "staff-approve");
    try { const r = await sr.staffApproveCreatives(plan.id, channels); setPlan(r.growth_plan); toast.success(`${r.approved} creative(s) approved — ready to launch`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
    finally { setReviewBusy(""); }
  };
  const review = async (channel, decision, note) => {
    setReviewBusy(`${decision}-${channel || "all"}`);
    try {
      const r = await sr.reviewCreatives(plan.id, { channel, decision, note });
      setPlan(r.growth_plan); setChangesFor(null); setChangeNote("");
      toast.success(decision === "approve" ? "Creative approved" : "Change request sent to your team");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setReviewBusy(""); }
  };
  const designAll = async () => {
    const todo = keys.filter((k) => !creatives[k]);
    if (!todo.length) { toast.info("Every campaign type already has a creative"); return; }
    setDesignAllBusy(true);
    let done = 0;
    for (const k of todo) {
      setDesignAllProg({ done, total: todo.length, current: labelFor(k) });
      try {
        const r = await sr.addPlanCreative(plan.id, { source: "ai", channel: k });
        setPlan(r.growth_plan);
      } catch (e) { toast.error(`${labelFor(k)}: ${e?.response?.data?.detail || "couldn't design"}`); }
      done += 1;
      setDesignAllProg({ done, total: todo.length, current: null });
    }
    setDesignAllBusy(false);
    setDesignAllProg(null);
    toast.success("AI designed an ad for every campaign type — scroll to review");
  };

  return (
    <div className="sr-card" style={{ marginTop: 14 }} data-testid="plan-creative-studio">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 6 }}><Eye size={13} /> Review creatives · one per campaign type</div>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
          {editable && undesignedKeys.length > 0 && (
            <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={designAll} disabled={designAllBusy} data-testid="design-all-btn">
              {designAllBusy
                ? <><Loader2 size={14} className="sr-spin" /> {designAllProg ? `Designing ${designAllProg.current || ""} (${designAllProg.done}/${designAllProg.total})…` : "Designing…"}</>
                : <><Wand2 size={14} /> Design all with AI ({undesignedKeys.length})</>}
            </button>
          )}
          <span className="sr-muted" style={{ fontSize: 12 }} data-testid="creative-studio-count">{designedCount}/{keys.length} designed</span>
        </div>
      </div>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 12 }}>
        {isStaff ? "Design a creative for each campaign type, then send them to the client for review before launch."
                 : "Review the creative for each campaign type. Pick AI options or upload your own — one creative is enough to launch."}
      </p>

      {/* Client review call-out */}
      {!isStaff && pendingKeys.length > 0 && (
        <div className="sr-card" style={{ background: "var(--gold-light, #FDF9EC)", border: "1px solid var(--gold)", marginBottom: 12 }} data-testid="client-review-callout">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <strong style={{ color: "var(--navy)" }}><Clock size={14} /> {pendingKeys.length} creative(s) awaiting your review</strong>
            <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => review(null, "approve")} disabled={!!reviewBusy} data-testid="client-approve-all-btn">
              {reviewBusy === "approve-all" ? <Loader2 size={14} className="sr-spin" /> : <><Check size={14} /> Approve all</>}
            </button>
          </div>
        </div>
      )}

      {/* Staff review bar: ask for client review, or (admin/manager) skip & approve */}
      {isStaff && editable && unapprovedKeys.length > 0 && (
        <div className="sr-card" style={{ background: "var(--blue-light)", border: "1px solid var(--blue)", marginBottom: 12 }} data-testid="staff-send-review-bar">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "var(--navy)" }}>
              {draftKeys.length > 0
                ? `${draftKeys.length} creative(s) not yet sent to the client.`
                : `${pendingKeys.length} creative(s) awaiting the client's review.`}
              {isApprover ? " You can send for review or approve now." : ""}
            </span>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {draftKeys.length > 0 && (
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={sendForReview} disabled={!!reviewBusy} data-testid="send-for-review-btn">
                  {reviewBusy === "send" ? <Loader2 size={14} className="sr-spin" /> : <><Send size={14} /> Send for client review</>}
                </button>
              )}
              {isApprover && (
                <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => staffApprove(null)} disabled={!!reviewBusy} data-testid="staff-approve-all-btn">
                  {reviewBusy === "staff-approve" ? <Loader2 size={14} className="sr-spin" /> : <><Check size={14} /> Skip review &amp; approve</>}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))" }}>
        {keys.map((k) => {
          const c = creatives[k];
          const meta = c ? (REVIEW_META[c.review_status] || REVIEW_META.draft) : null;
          const canReview = !isStaff && c && ["pending_review", "changes_requested"].includes(c.review_status);
          return (
            <div key={k} className="sr-card" style={{ background: "var(--surface,#fff)" }} data-testid={`creative-row-${k}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 6 }}>
                <strong style={{ color: "var(--navy)", fontSize: 14 }}>{labelFor(k)}</strong>
                {c ? (
                  <span className={`sr-badge ${meta.cls}`} data-testid={`creative-status-${k}`}><meta.Icon size={11} /> {meta.label}</span>
                ) : (
                  <span className="sr-badge sr-badge-gray" data-testid={`creative-status-${k}`}>Not designed yet</span>
                )}
              </div>
              {c ? (
                <>
                  <CreativePreview channelKey={k} creative={c} bizName={bizName} planId={plan.id} canDownload={canDownload} />
                  {c.created_by_staff && c.created_by_name && <p className="sr-muted" style={{ fontSize: 11, marginTop: 8 }}>By {c.created_by_name} (your team)</p>}
                  {c.staff_approved && <p className="sr-muted" style={{ fontSize: 11, marginTop: 4 }} data-testid={`creative-staff-approved-${k}`}><Check size={11} /> Approved by {c.staff_approved_by_name || "your team"} — client review skipped{fmtWhen(c.reviewed_at)}</p>}
                  {c.review_status === "approved" && !c.staff_approved && c.reviewed_by_name && (
                    <p className="sr-muted" style={{ fontSize: 11, marginTop: 4 }} data-testid={`creative-approved-by-${k}`}><CheckCircle2 size={11} /> Approved by {c.reviewed_by_name} (client){fmtWhen(c.reviewed_at)}</p>
                  )}
                  {c.review_status === "changes_requested" && (
                    <p style={{ fontSize: 12, marginTop: 8, color: "#B42318" }} data-testid={`creative-client-note-${k}`}><MessageSquare size={11} /> Changes requested by {c.reviewed_by_name || "client"}{fmtWhen(c.reviewed_at)}{c.client_note ? ` — "${c.client_note}"` : ""}</p>
                  )}
                  {/* Client review actions */}
                  {canReview && (
                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => review(k, "approve")} disabled={!!reviewBusy} data-testid={`creative-approve-${k}`}>
                        {reviewBusy === `approve-${k}` ? <Loader2 size={14} className="sr-spin" /> : <><Check size={14} /> Approve</>}
                      </button>
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setChangesFor(k); setChangeNote(""); }} disabled={!!reviewBusy} data-testid={`creative-request-changes-${k}`}><MessageSquare size={14} /> Request changes</button>
                    </div>
                  )}
                  {canReview && changesFor === k && (
                    <div style={{ marginTop: 8 }} data-testid={`creative-changes-form-${k}`}>
                      <textarea className="sr-input" rows={2} value={changeNote} onChange={(e) => setChangeNote(e.target.value)} placeholder="What would you like changed?" data-testid={`creative-changes-note-${k}`} />
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setChangesFor(null)}>Cancel</button>
                        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => review(k, "changes", changeNote)} disabled={!!reviewBusy} data-testid={`creative-changes-submit-${k}`}>Send request</button>
                      </div>
                    </div>
                  )}
                  {/* Editing actions */}
                  {editable && (
                    <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {isApprover && c.review_status !== "approved" && (
                        <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => staffApprove([k])} disabled={!!reviewBusy} data-testid={`creative-staff-approve-${k}`}>
                          {reviewBusy === `staff-approve-${k}` ? <Loader2 size={13} className="sr-spin" /> : <><Check size={13} /> Approve now</>}
                        </button>
                      )}
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setDesignCh(k)} data-testid={`creative-redesign-${k}`}><Wand2 size={13} /> New AI options</button>
                      {c.format === "script" && (
                        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setVoicePickerFor(k)} data-testid={`creative-voiceover-${k}`}>
                          <Volume2 size={13} /> {c.voiceover_url ? "Change / regenerate voice" : "Choose a voice"}
                        </button>
                      )}
                      {(k === "video" || k === "ctv") && (
                        <>
                          <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={() => setVideoPickerFor(k)} disabled={c.video_status === "processing"} data-testid={`creative-render-video-${k}`}>
                            {c.video_status === "processing" ? <Loader2 size={13} className="sr-spin" /> : <Video size={13} />} {c.video_url ? "Re-render video" : (c.video_status === "processing" ? "Rendering…" : "Generate AI video")}
                          </button>
                          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => genScenes(k)} disabled={!!mediaBusy} data-testid={`creative-scene-images-${k}`}>
                            {mediaBusy === `scene-${k}` ? <Loader2 size={13} className="sr-spin" /> : <ImageIcon size={13} />} {c.scene_images?.length ? "Regenerate scene imagery" : "Generate scene imagery"}
                          </button>
                          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => genMontage(k)} disabled={!!mediaBusy} data-testid={`creative-montage-${k}`}>
                            {mediaBusy === `montage-${k}` ? <Loader2 size={13} className="sr-spin" /> : <Film size={13} />} {c.montage_url ? "Rebuild combined video" : "Create combined video"}
                          </button>
                          <MusicControl planId={plan.id} channel={k} creative={c} onUpdate={setPlan} vol={musicVol[k]} onVol={(v) => setMusicVol((m) => ({ ...m, [k]: v }))} disabled={!!mediaBusy} />
                        </>
                      )}
                      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => removeCreative(k)} disabled={rowBusy === k} data-testid={`creative-remove-${k}`}>{rowBusy === k ? <Loader2 size={13} className="sr-spin" /> : <Trash2 size={13} />}</button>
                    </div>
                  )}
                </>
              ) : (
                editable ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setDesignCh(k)} data-testid={`creative-design-${k}`}><Wand2 size={13} /> Design with AI</button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => openUpload(k)} data-testid={`creative-upload-${k}`}><Upload size={13} /> Upload</button>
                  </div>
                ) : <p className="sr-muted" style={{ fontSize: 12 }}>No creative for this channel.</p>
              )}

              {/* Inline upload form */}
              {editable && uploadCh === k && (
                <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10, display: "grid", gap: 8 }} data-testid={`creative-upload-form-${k}`}>
                  <input className="sr-input" value={upForm.headline} onChange={(e) => setUpForm((f) => ({ ...f, headline: e.target.value }))} placeholder="Headline" data-testid={`creative-upload-headline-${k}`} />
                  <input className="sr-input" value={upForm.body} onChange={(e) => setUpForm((f) => ({ ...f, body: e.target.value }))} placeholder="Body (optional)" />
                  <input className="sr-input" value={upForm.cta} onChange={(e) => setUpForm((f) => ({ ...f, cta: e.target.value }))} placeholder="Call to action (optional)" />
                  <input className="sr-input" value={upForm.image_url} onChange={(e) => setUpForm((f) => ({ ...f, image_url: e.target.value }))} placeholder="Image URL (optional)" />
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setUploadCh(null)}>Cancel</button>
                    <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={saveUpload} disabled={saveBusy === "upload"} data-testid={`creative-upload-save-${k}`}>{saveBusy === "upload" ? <Loader2 size={14} className="sr-spin" /> : "Save"}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {designCh && (
        <VariantPicker planId={plan.id} channelKey={designCh} channelLabel={labelFor(designCh)}
          onClose={() => setDesignCh(null)} onPicked={(gp) => { setPlan(gp); setDesignCh(null); toast.success("Creative selected"); }} />
      )}
      {voicePickerFor && (
        <VoicePicker planId={plan.id} channelKey={voicePickerFor}
          onClose={() => setVoicePickerFor(null)}
          onDone={(gp) => { setPlan(gp); setVoicePickerFor(null); toast.success("Voiceover ready — press play"); }} />
      )}
      {videoPickerFor && (
        <VideoPicker planId={plan.id} channelKey={videoPickerFor} channelLabel={labelFor(videoPickerFor)}
          onClose={() => setVideoPickerFor(null)}
          onStarted={(gp) => { setPlan(gp); setVideoPickerFor(null); toast.success("Rendering your video — this can take 1–3 minutes"); }} />
      )}
    </div>
  );
}


function MediaMixBlock({ plan }) {
  const [scenarios, setScenarios] = useState(null);
  const [flights, setFlights] = useState([]);
  const [reclaim, setReclaim] = useState(null);
  useEffect(() => {
    if (!plan?.id) return;
    sr.listMixScenarios(plan.id).then((r) => setScenarios(r.scenarios || [])).catch(() => setScenarios([]));
    sr.radioFlights().then((r) => setFlights((r.flights || []).filter((f) => f.plan_id === plan.id))).catch(() => setFlights([]));
    sr.getBudgetReclaim(plan.id).then(setReclaim).catch(() => {});
  }, [plan?.id]);
  const mix = plan?.media_mix;
  const pdata = plan?.plan || {};
  const hasChosen = !!(mix?.allocations && Object.values(mix.allocations).some((v) => Number(v) > 0));
  let entries = [];
  if (hasChosen) {
    entries = Object.entries(mix.allocations).filter(([, v]) => Number(v) > 0).sort((a, b) => Number(b[1]) - Number(a[1]));
  } else if ((pdata.channel_mix || []).length) {
    const b = Number(plan?.monthly_budget) || 0;
    entries = (pdata.channel_mix || [])
      .map((c) => [c.channel, Math.round(b * (Number(c.budget_pct) || 0) / 100)])
      .filter(([, v]) => Number(v) > 0)
      .sort((a, b2) => Number(b2[1]) - Number(a[1]));
  }
  const total = hasChosen ? (mix.total || entries.reduce((a, [, v]) => a + Number(v), 0)) : entries.reduce((a, [, v]) => a + Number(v), 0);
  const slug = (k) => String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const maxImp = Math.max(1, ...(scenarios || []).map((s) => Number(s.totals?.gross_impression_opportunities || 0)));
  const STEPS = ["Draft", "Confirmed", "Airing"];
  const STATUS_STEP = { DRAFT: 0, SCHEDULED: 1, SYNCED: 1, PAUSED: 1, LIVE: 2, ENDED: 2 };
  return (
    <div className="sr-card" style={{ marginTop: 14 }} data-testid="plan-media-mix">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div className="sr-eyebrow" style={{ marginBottom: 0, display: "inline-flex", alignItems: "center", gap: 6 }}><RadioIco size={13} /> Your media mix</div>
        <Link to={`/app/media-mix?plan=${plan.id}`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="plan-edit-mix">{hasChosen ? "Edit media mix" : "Shape media mix"}</Link>
      </div>
      {reclaim?.has_stranded && (
        <Link to={`/app/media-mix?plan=${plan.id}&reclaim=1`} data-testid="plan-reclaim-prompt"
          style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none",
            background: "#FFF8EC", border: "1px solid #F3D9A4", borderRadius: 10, padding: "9px 12px", marginBottom: 10 }}>
          <Recycle size={15} style={{ color: "#B8791B", flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 12.5, color: "var(--navy)" }}>
            <strong>{usdShort(reclaim.stranded_total)} unused budget</strong> — reclaim it across your active channels
          </span>
          <span className="sr-badge sr-badge-amber" style={{ fontSize: 11 }}>Reclaim →</span>
        </Link>
      )}
      {entries.length === 0 ? (
        <p className="sr-muted" style={{ fontSize: 13 }} data-testid="plan-mix-empty">You haven't shaped a channel-by-channel media mix yet. Open the planner to split this budget across channels and see the potential reach.</p>
      ) : (
        <>
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }} data-testid="plan-mix-source">{hasChosen ? `Chosen allocation · ${usdShort(total)}/mo` : `Recommended split from your plan · ${usdShort(total)}/mo · open the planner to fine-tune`}</div>
          {entries.map(([k, v]) => {
            const pct = total > 0 ? Math.round((Number(v) / total) * 100) : 0;
            return (
              <div key={k} style={{ marginBottom: 8 }} data-testid={`plan-mix-${slug(k)}`}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <strong style={{ color: "var(--navy)" }}>{chLabel(k)}</strong><span>{usdShort(v)} · {pct}%</span>
                </div>
                <div className="sr-progress" style={{ margin: "3px 0" }}><div style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </>
      )}

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
        <div className="sr-muted" style={{ fontSize: 12, marginBottom: 6 }}>Saved mixes — reach compared</div>
        {scenarios === null ? (
          <Loader2 size={14} className="sr-spin" />
        ) : scenarios.length === 0 ? (
          <p className="sr-muted" style={{ fontSize: 12 }} data-testid="plan-scenarios-empty">No saved mixes yet — the planner auto-saves the AI's recommended mix and any you name.</p>
        ) : (
          <div data-testid="plan-scenarios">
            {scenarios.map((s) => {
              const imp = Number(s.totals?.gross_impression_opportunities || 0);
              const w = Math.round((imp / maxImp) * 100);
              return (
                <div key={s.id} style={{ marginBottom: 8 }} data-testid={`plan-scenario-${s.id}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span style={{ color: "var(--navy)", fontWeight: s.source === "ai" ? 700 : 500 }}>{s.source === "ai" ? "★ " : ""}{s.name}</span>
                    <span className="sr-muted">{usdShort(s.totals?.customer_allocation)} · ~{imp.toLocaleString()} imp</span>
                  </div>
                  <div className="sr-progress" style={{ margin: "3px 0" }}><div style={{ width: `${w}%`, background: s.source === "ai" ? "var(--gold)" : "var(--blue)" }} /></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {flights.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }} data-testid="plan-radio-bookings">
          <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }}>Radio bookings</div>
          {flights.map((f) => {
            const step = STATUS_STEP[f.status] ?? 0;
            return (
              <div key={f.id} style={{ marginBottom: 12 }} data-testid={`plan-radio-flight-${f.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <strong style={{ color: "var(--navy)" }}>{f.station_name}</strong>
                  <span>{usdShort(f.budget_monthly)}/mo</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }} data-testid={`plan-radio-status-${f.id}`}>
                  {STEPS.map((label, i) => (
                    <React.Fragment key={label}>
                      <span style={{ fontSize: 11, fontWeight: i === step ? 800 : 600, padding: "3px 9px", borderRadius: 999,
                        background: i < step ? "var(--blue-light)" : i === step ? "var(--blue)" : "#EEF1F5",
                        color: i <= step ? "var(--navy)" : "var(--muted)" }}>{label}</span>
                      {i < STEPS.length - 1 && <span style={{ width: 14, height: 2, background: i < step ? "var(--blue)" : "#E3E7ED" }} />}
                    </React.Fragment>
                  ))}
                </div>
                <div className="sr-muted" style={{ fontSize: 11, marginTop: 4 }}>{f.total_spots ? `${f.total_spots} spots/mo · ` : ""}Booked with ops — confirmed with the stations before airing.</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlanGeoCard({ plan, editable, onSaved }) {
  const g = plan.geo_targeting || {};
  const [radius, setRadius] = useState(g.radius_miles ? String(g.radius_miles) : "");
  const [zips, setZips] = useState((g.zips || []).join(", "));
  const [dmas, setDmas] = useState(g.dmas || []);
  const [dmaOptions, setDmaOptions] = useState([]);
  const [dmaSearch, setDmaSearch] = useState("");
  const [cities, setCities] = useState((g.cities || []).join(", "));
  const [states, setStates] = useState((g.states || []).join(", "));
  const [countries, setCountries] = useState((g.countries || []).join(", "));
  const [busy, setBusy] = useState(false);
  useEffect(() => { sr.dmaList().then((r) => setDmaOptions(r.dmas || [])).catch(() => {}); }, []);
  const geoStrategy = (plan.plan || {}).geographic_strategy;
  const dmaFiltered = dmaOptions.filter((d) => d.name.toLowerCase().includes(dmaSearch.trim().toLowerCase()));
  const dmaName = (code) => (dmaOptions.find((d) => d.code === code) || {}).name || code;
  const save = async () => {
    setBusy(true);
    try {
      const zl = zips.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean);
      const parse = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
      const r = await sr.setPlanGeo(plan.id, {
        radius_miles: Number(radius) > 0 ? Number(radius) : null, zips: zl, dmas,
        cities: parse(cities), states: parse(states), countries: parse(countries),
      });
      onSaved(r.geo_targeting);
      toast.success("Geo targeting saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const hasGeo = !!(g.radius_miles || (g.zips || []).length || (g.dmas || []).length || (g.cities || []).length || (g.states || []).length || (g.countries || []).length);
  return (
    <div className="sr-card" style={{ marginTop: 14 }} data-testid="plan-geo-card">
      <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><MapPin size={13} /> Geo targeting · where your ads run</div>
      {geoStrategy && <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 6, fontStyle: "italic" }}>AI recommendation: {geoStrategy}</p>}
      {!editable ? (
        <div style={{ marginTop: 10 }} data-testid="plan-geo-summary">
          {hasGeo ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {g.radius_miles ? <span className="sr-badge sr-badge-blue">{g.radius_miles} mi radius</span> : null}
              {(g.zips || []).map((z) => <span key={z} className="sr-badge sr-badge-gray">{z}</span>)}
              {(g.dmas || []).map((d) => <span key={d} className="sr-badge sr-badge-gray">{dmaName(d)}</span>)}
              {(g.cities || []).map((c) => <span key={`c-${c}`} className="sr-badge sr-badge-gray">{c}</span>)}
              {(g.states || []).map((s) => <span key={`s-${s}`} className="sr-badge sr-badge-gray">{s}</span>)}
              {(g.countries || []).map((c) => <span key={`co-${c}`} className="sr-badge sr-badge-blue">{c}</span>)}
            </div>
          ) : <p className="sr-muted" style={{ fontSize: 13 }}>No geo targeting set.</p>}
        </div>
      ) : (
        <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 88 }}>Radius</span>
            <input className="sr-input" type="number" min="0" step="0.1" value={radius} onChange={(e) => setRadius(e.target.value)} placeholder="e.g. 5" data-testid="plan-geo-radius" style={{ width: 110 }} />
            <span className="sr-muted" style={{ fontSize: 12 }}>miles around each location</span>
            {[3, 5, 10, 25].map((m) => (
              <button key={m} type="button" onClick={() => setRadius(String(m))} data-testid={`plan-geo-radius-${m}`}
                style={{ border: "1px solid var(--border)", background: "#fff", borderRadius: 999, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>{m} mi</button>
            ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 88 }}>Zip codes</span>
            <input className="sr-input" value={zips} onChange={(e) => setZips(e.target.value)} placeholder="e.g. 75070, 75071, 75025" data-testid="plan-geo-zips" style={{ flex: 1, minWidth: 220 }} />
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 88 }}>DMA / metro</span>
              <input className="sr-input" value={dmaSearch} onChange={(e) => setDmaSearch(e.target.value)} placeholder="Search metros…" data-testid="plan-geo-dma-search" style={{ flex: 1, minWidth: 220 }} />
            </div>
            {dmas.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {dmas.map((code) => (
                  <button key={code} type="button" onClick={() => setDmas((cur) => cur.filter((x) => x !== code))} data-testid={`plan-geo-dma-selected-${code}`}
                    style={{ border: "1px solid var(--blue)", background: "var(--blue)", color: "#fff", borderRadius: 999, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>{dmaName(code)} ✕</button>
                ))}
              </div>
            )}
            {dmaSearch.trim() && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                {dmaFiltered.slice(0, 18).map((d) => {
                  const on = dmas.includes(d.code);
                  return (
                    <button key={d.code} type="button" onClick={() => setDmas((cur) => on ? cur.filter((x) => x !== d.code) : [...cur, d.code])} data-testid={`plan-geo-dma-${d.code}`}
                      style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>{d.name}</button>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 10, display: "grid", gap: 10 }} data-testid="plan-geo-broad">
            <div className="sr-muted" style={{ fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.4 }}>Broad scope — for national / multi-market players (combinable with the above)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 88 }}>Cities</span>
              <input className="sr-input" value={cities} onChange={(e) => setCities(e.target.value)} placeholder="e.g. Dallas, Austin, Toronto" data-testid="plan-geo-cities" style={{ flex: 1, minWidth: 220 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 88 }}>States</span>
              <input className="sr-input" value={states} onChange={(e) => setStates(e.target.value)} placeholder="e.g. Texas, California, Ontario" data-testid="plan-geo-states" style={{ flex: 1, minWidth: 220 }} />
            </div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, color: "var(--navy)", minWidth: 88, marginTop: 8 }}>Countries</span>
              <div style={{ flex: 1, minWidth: 220 }}>
                <input className="sr-input" value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="e.g. United States, Canada (entire country = national)" data-testid="plan-geo-countries" style={{ width: "100%" }} />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                  {["United States", "Canada", "United Kingdom", "Australia", "India"].map((c) => (
                    <button key={c} type="button" data-testid={`plan-geo-country-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setCountries((cur) => { const list = cur.split(",").map((x) => x.trim()).filter(Boolean); return list.some((x) => x.toLowerCase() === c.toLowerCase()) ? cur : [...list, c].join(", "); })}
                      style={{ border: "1px solid var(--border)", background: "#fff", borderRadius: 999, padding: "3px 10px", fontSize: 12, cursor: "pointer" }}>+ {c}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={save} disabled={busy} data-testid="plan-geo-save">{busy ? <Loader2 size={14} className="sr-spin" /> : "Save geo targeting"}</button>
          </div>
        </div>
      )}
    </div>
  );
}


export default function GrowthPlan() {
  const [params] = useSearchParams();
  const [plan, setPlan] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [budget, setBudget] = useState(3000);
  const [intendedBudget, setIntendedBudget] = useState(null);
  const [bizName, setBizName] = useState("");
  const [canDownload, setCanDownload] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createBudget, setCreateBudget] = useState(3000);
  const [createBrief, setCreateBrief] = useState("");
  const [createChannels, setCreateChannels] = useState(MEDIA_CHANNELS.map((c) => c.key));
  const [promoteScope, setPromoteScope] = useState("whole_business");
  const [promoteItems, setPromoteItems] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [releases, setReleases] = useState([]);
  const [promoteCollection, setPromoteCollection] = useState("");
  const [promoteRelease, setPromoteRelease] = useState("");
  const [planIndustry, setPlanIndustry] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [r, home] = await Promise.all([sr.listPlans(), sr.home().catch(() => null)]);
      const intended = Number(home?.tenant?.intended_monthly_budget) || null;
      if (intended) setIntendedBudget(intended);
      setBizName(home?.profile?.name || home?.tenant?.name || "");
      setPlanIndustry(home?.workflow?.industry || home?.profile?.industry || home?.tenant?.industry || "");
      setCanDownload((Number(home?.live_campaigns) || 0) > 0);
      const list = r.growth_plans || [];
      const id = params.get("id");
      let p = (id && list.find((x) => x.id === id)) || list[0] || null;
      if (p?.id) {
        try { const g = await sr.getPlan(p.id); if (g?.growth_plan) p = g.growth_plan; } catch { /* keep list item */ }
      }
      setVersions(p && !list.find((x) => x.id === p.id) ? [p, ...list] : list);
      setPlan(p);
      setBudget(p ? p.monthly_budget : (intended || 3000));
    } catch { toast.error("Could not load plan"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [params]);

  const selectVersion = async (id) => {
    const v = versions.find((x) => x.id === id); if (!v) return;
    setPlan(v); setBudget(v.monthly_budget);
    try { const g = await sr.getPlan(id); if (g?.growth_plan) { setPlan(g.growth_plan); setBudget(g.growth_plan.monthly_budget); } } catch { /* keep list item */ }
  };
  const approve = async () => { setBusy(true); try { const r = await sr.approvePlan(plan.id); setPlan(r.growth_plan); toast.success("Plan approved"); load(); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const activate = async () => { setBusy(true); try { const r = await sr.activatePlan(plan.id); toast.success(`${r.campaigns.length} campaigns submitted`); const g = await sr.getPlan(plan.id); setPlan(g.growth_plan); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); } };
  const revise = async () => { setBusy(true); try { const r = await sr.revisePlan(plan.id, { monthly_budget: Number(budget) }); toast.success("Revised — a new version was created"); await load(); setPlan(r.growth_plan); } catch { toast.error("Failed"); } finally { setBusy(false); } };
  const openCreate = () => {
    setCreateBudget(plan?.monthly_budget || intendedBudget || budget || 3000);
    setCreateBrief(plan?.brief || "");
    setCreateChannels(plan?.channels?.length ? plan.channels : MEDIA_CHANNELS.map((c) => c.key));
    setPromoteScope("whole_business"); setPromoteItems([]);
    sr.catalogItems({ status: "active" }).then((r) => setCatalogItems(r.items || [])).catch(() => setCatalogItems([]));
    setShowCreate(true);
  };
  const submitCreate = async () => {
    if (!createChannels.length) { toast.error("Pick at least one channel"); return; }
    if (promoteScope === "items" && !promoteItems.length) { toast.error("Select at least one item to promote"); return; }
    setBusy(true);
    try {
      const locs = await sr.locations();
      const sel = catalogItems.filter((i) => promoteItems.includes(i.id));
      const r = await sr.generatePlan({ goal: plan?.goal || "visits", monthly_budget: Number(createBudget),
        location_id: locs.locations?.[0]?.id, channels: createChannels, brief: createBrief.trim() || null,
        promote_scope: promoteScope,
        promoted_item_ids: promoteScope === "items" ? promoteItems : undefined,
        destination_url: sel[0]?.destination_url || undefined });
      toast.success(r.ai_available === false ? "Draft plan created (AI was busy — refine before launch)" : `Plan created (Version ${r.growth_plan.version})`);
      setShowCreate(false); await load(); setPlan(r.growth_plan);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create a plan"); }
    finally { setBusy(false); }
  };

  const createModal = showCreate ? (
    <div data-testid="create-plan-modal" onClick={() => !busy && setShowCreate(false)} style={{ position: "fixed", inset: 0, zIndex: 60, background: "rgba(16,24,40,.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div className="sr-card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 20, color: "var(--navy)", margin: 0 }}>Create a growth plan</h2>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setShowCreate(false)} data-testid="create-plan-close"><X size={16} /></button>
        </div>
        <div className="sr-field" style={{ marginBottom: 14 }}>
          <label className="sr-label">Monthly budget</label>
          <input className="sr-input" type="number" min="0" value={createBudget} onChange={(e) => setCreateBudget(e.target.value)} data-testid="create-plan-budget" />
        </div>
        <label className="sr-label" style={{ display: "block", marginBottom: 8 }}>Media channels for this plan</label>
        <ChannelPicker value={createChannels} onChange={setCreateChannels} testid="create-plan-channels" />
        <div className="sr-field" style={{ marginTop: 14 }} data-testid="create-plan-promote">
          <label className="sr-label">What should this plan promote?</label>
          <div style={{ display: "flex", gap: 8, marginBottom: promoteScope === "items" ? 10 : 0 }}>
            {[{ k: "whole_business", l: "The whole business" }, { k: "items", l: "Specific items" }].map((o) => {
              const on = promoteScope === o.k;
              return (
                <button key={o.k} type="button" onClick={() => setPromoteScope(o.k)} data-testid={`create-plan-scope-${o.k}`}
                  style={{ border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), background: on ? "var(--blue)" : "#fff", color: on ? "#fff" : "var(--navy)", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                  {o.l}
                </button>
              );
            })}
          </div>
          {promoteScope === "items" && (
            catalogItems.length === 0
              ? <p className="sr-muted" style={{ fontSize: 12 }}>No active catalog items yet — add some in the Catalog, or promote the whole business.</p>
              : (
                <div style={{ display: "grid", gap: 6, maxHeight: 160, overflowY: "auto" }}>
                  {catalogItems.map((it) => {
                    const on = promoteItems.includes(it.id);
                    return (
                      <label key={it.id} data-testid={`create-plan-item-${it.id}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--navy)", cursor: "pointer", padding: "6px 8px", border: "1px solid " + (on ? "var(--blue)" : "var(--border)"), borderRadius: 8 }}>
                        <input type="checkbox" checked={on} onChange={() => setPromoteItems((p) => on ? p.filter((x) => x !== it.id) : [...p, it.id])} />
                        <span style={{ flex: 1 }}>{it.name}</span>
                        <span className="sr-muted" style={{ fontSize: 11 }}>{it.item_type}</span>
                      </label>
                    );
                  })}
                </div>
              )
          )}
        </div>
        <div className="sr-field" style={{ marginTop: 14 }}>
          <label className="sr-label">Targeting brief <span className="sr-muted" style={{ fontWeight: 400 }}>(optional)</span></label>
          <textarea className="sr-input" rows={3} value={createBrief} onChange={(e) => setCreateBrief(e.target.value)}
            data-testid="create-plan-brief"
            placeholder="What should this campaign achieve? Who to reach, where, and the result you want." />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
          <button className="sr-btn sr-btn-ghost" onClick={() => setShowCreate(false)} disabled={busy} data-testid="create-plan-cancel">Cancel</button>
          <button className="sr-btn sr-btn-primary" onClick={submitCreate} disabled={busy} data-testid="create-plan-submit">{busy ? <Loader2 size={15} className="sr-spin" /> : <><Sparkles size={15} /> Build plan</>}</button>
        </div>
      </div>
    </div>
  ) : null;

  if (loading) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;

  if (!plan) return (
    <div>
      <h1 style={{ fontSize: 30 }}>Growth Plan</h1>
      <div className="sr-card sr-empty" style={{ marginTop: 20 }} data-testid="plan-empty">
        <Sparkles size={28} color="var(--blue)" />
        <p style={{ marginTop: 10 }}>No growth plan yet. Generate one, or start from a proven industry playbook.</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 16 }}>
          <Link to="/app/onboarding" className="sr-btn sr-btn-ghost sr-btn-sm">Go to setup</Link>
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={openCreate} disabled={busy} data-testid="plan-generate-btn">
            {busy ? <Loader2 size={15} className="sr-spin" /> : "Generate plan"}
          </button>
        </div>
      </div>
      <PlaybookPicker onApplied={load} />
      {planIndustry === "luxury" && (
        <div style={{ marginTop: 16 }} data-testid="growth-plan-luxury-empty"><LuxuryPlaybook onApplied={load} /></div>
      )}
      {createModal}
    </div>
  );

  const p = plan.plan || {};
  const approved = plan.status === "APPROVED";
  const activating = plan.status === "ACTIVATING" || plan.activated_at;
  const creativeReady = !!plan.creative_ready;
  const planChannelKeys = [...new Set((plan.channels?.length ? plan.channels : (p.channel_mix || []).map((c) => chKeyFromName(c.channel))).filter(Boolean))];
  const pendingReview = Object.values(plan.creatives || {}).some((c) => ["pending_review", "changes_requested"].includes(c?.review_status));
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 30 }}>Growth Plan</h1>
          <p className="sr-muted" style={{ marginTop: 4 }}>
            Version {plan.version} · Goal: <span style={{ textTransform: "capitalize" }}>{plan.goal}</span> ·
            <span className={`sr-badge ${activating ? "sr-badge-green" : approved ? "sr-badge-green" : "sr-badge-amber"}`} style={{ marginLeft: 8 }}>{plan.status}</span>
            {p._fallback && <span className="sr-badge sr-badge-amber" style={{ marginLeft: 6 }}><AlertTriangle size={12} /> Template estimate</span>}
          </p>
          {planChannelKeys.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }} data-testid="plan-channel-badges">
              {planChannelKeys.map((k) => (
                <span key={k} className="sr-badge sr-badge-gray" data-testid={`plan-channel-${k}`}>{CH_BADGE_LABEL[k] || k}</span>
              ))}
            </div>
          )}
          {versions.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
              <span className="sr-muted" style={{ fontSize: 12 }}>History:</span>
              <select className="sr-select" value={plan.id} onChange={(e) => selectVersion(e.target.value)}
                data-testid="plan-version-select" style={{ maxWidth: 320, fontSize: 13 }}>
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>{`Version ${v.version} · ${v.status}${v.goal ? ` · ${v.goal}` : ""}`}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {!approved && !activating && <button className="sr-btn sr-btn-primary" onClick={approve} disabled={busy} data-testid="plan-approve-btn"><Check size={16} /> Approve plan</button>}
          {approved && !activating && !creativeReady && <span className="sr-badge sr-badge-amber" data-testid="plan-needs-creative" style={{ alignSelf: "center" }}>{pendingReview ? <><Clock size={13} /> Awaiting client approval</> : <><Wand2 size={13} /> Add a creative to launch</>}</span>}
          {approved && !activating && creativeReady && <button className="sr-btn sr-btn-gold" onClick={activate} disabled={busy} data-testid="plan-activate-btn"><Check size={16} /> Activate & launch</button>}
          {activating && <Link to="/app/campaigns" className="sr-btn sr-btn-primary" data-testid="plan-view-campaigns"><Megaphone size={16} /> View campaigns</Link>}
          <button className="sr-btn sr-btn-ghost" onClick={openCreate} disabled={busy} data-testid="plan-new-btn"><Sparkles size={16} /> New plan</button>
        </div>
      </div>
      {activating && <PlanUpliftCard planId={plan.id} />}
      {(approved || activating) && (
        <CreativeStudio plan={plan} channelKeys={planChannelKeys} editable={approved && !activating} setPlan={setPlan} bizName={bizName} canDownload={canDownload} />
      )}
      {(approved || activating) && (
        <PlanGeoCard plan={plan} editable={approved && !activating} onSaved={(geo) => setPlan((p) => ({ ...p, geo_targeting: geo }))} />
      )}
      {(approved || activating) && (
        <div style={{ marginTop: 14 }}>
          <EventTargeting planId={plan.id} plan={plan} editable={approved && !activating} onSaved={(gp) => gp && setPlan(gp)} />
        </div>
      )}
      <LifeSciencesTargeting />
      <StrategyTracker />
      <BonusInventory compact />
      {activating && (
        <div className="sr-card" style={{ marginTop: 14, background: "var(--blue-light)", border: "1px solid var(--blue)", display: "flex", gap: 10, alignItems: "center" }}>
          <Check size={18} color="var(--success)" />
          <p style={{ fontSize: 14 }}>Campaigns submitted. Live delivery is confirmed by operations before anything shows as live — nothing is faked.</p>
        </div>
      )}

      {plan.ga_snapshot && (
        <div className="sr-card" style={{ marginTop: 14, borderLeft: "4px solid #E8710A" }} data-testid="plan-ga-panel">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Your real website traffic · {plan.ga_snapshot.period}</div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div><span className="sr-stat-val" data-testid="plan-ga-sessions">{Number(plan.ga_snapshot.sessions || 0).toLocaleString()}</span> <span className="sr-muted">sessions</span></div>
            <div><span className="sr-stat-val">{Number(plan.ga_snapshot.users || 0).toLocaleString()}</span> <span className="sr-muted">users</span></div>
            <div><span className="sr-stat-val">{Number(plan.ga_snapshot.conversions || 0).toLocaleString()}</span> <span className="sr-muted">conversions</span></div>
          </div>
          {(plan.ga_snapshot.top_pages || []).length > 0 && (
            <p className="sr-muted" style={{ fontSize: 13, marginTop: 8 }}>Top pages: {plan.ga_snapshot.top_pages.slice(0, 3).map((p) => p.page).join(" · ")}</p>
          )}
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>These recommendations factor in your actual site traffic.</p>
        </div>
      )}

      <Block title="Business diagnosis"><p>{p.diagnosis}</p></Block>
      <Block title="Local market"><p>{p.local_market}</p></Block>

      {planIndustry === "luxury" && (
        <div data-testid="growth-plan-luxury"><LuxuryPlaybook onApplied={load} /></div>
      )}

      {AUDIENCE_CATALOG_INDUSTRIES[planIndustry] && (
        <div style={{ marginTop: 14 }} data-testid="growth-plan-audiences">
          <AudienceBrowser industry={planIndustry} industryLabel={AUDIENCE_CATALOG_INDUSTRIES[planIndustry]} defaultBudget={Number(budget) || 3000} />
        </div>
      )}

      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 14 }}>
        <div className="sr-card">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Proposed offer</div>
          <strong style={{ color: "var(--navy)" }}>{p.offer?.headline}</strong>
          <p className="sr-muted" style={{ marginTop: 6, fontSize: 14 }}>{p.offer?.detail}</p>
          <p style={{ marginTop: 8, fontSize: 13, color: "var(--success)" }}>{p.offer?.margin_note}</p>
        </div>
        <div className="sr-card">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Forecast (a range, not a guarantee)</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span className="sr-stat-val">{p.forecast?.low}–{p.forecast?.high}</span>
            <span className="sr-muted">{p.forecast?.unit}</span>
            <span className={`sr-badge ${CONF[p.forecast?.confidence] || "sr-badge-gray"}`}>{p.forecast?.confidence}</span>
          </div>
          <div className="sr-muted" style={{ fontSize: 12, marginTop: 6, textTransform: "capitalize" }}>{p.forecast?.metric}</div>
          {p.forecast?.assumptions?.length > 0 && (
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="plan-forecast-assumptions">{p.forecast.assumptions.join(" · ")}</p>
          )}
          {p.forecast?.limitations?.length > 0 && (
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}><AlertTriangle size={11} /> {p.forecast.limitations.join(" ")}</p>
          )}
        </div>
      </div>

      <Block title="Channel mix">
        {(p.channel_mix || []).map((c) => (
          <div key={c.channel} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14 }}>
              <strong style={{ color: "var(--navy)" }}>{c.channel}</strong><span>{c.budget_pct}%</span>
            </div>
            <div className="sr-progress" style={{ margin: "4px 0" }}><div style={{ width: `${c.budget_pct}%` }} /></div>
            <div className="sr-muted" style={{ fontSize: 12 }}>{c.why}</div>
          </div>
        ))}
      </Block>

      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 14 }}>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}><TargetIco size={13} /> Audience</div><p style={{ fontSize: 14 }}>{p.audience_strategy}</p></div>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}><MapPin size={13} /> Geography</div><p style={{ fontSize: 14 }}>{p.geographic_strategy}</p></div>
      </div>

      <Block title="Budget allocation">
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          {Object.entries(p.budget_allocation || {}).map(([k, v]) => (
            <div key={k}><div className="sr-stat-val" style={{ fontSize: 20 }}>${Number(v).toLocaleString()}</div>
              <div className="sr-stat-label" style={{ textTransform: "capitalize" }}>{k.replace(/_/g, " ")}</div></div>
          ))}
        </div>
      </Block>

      <MediaMixBlock plan={plan} />

      <div className="sr-grid" style={{ gridTemplateColumns: "1fr 1fr", marginTop: 14 }}>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>Creative set</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14 }}>{(p.creative_set || []).map((c, i) => <li key={i}>{c}</li>)}</ul></div>
        <div className="sr-card"><div className="sr-eyebrow" style={{ marginBottom: 8 }}>Measurement</div>
          <p style={{ fontSize: 14 }}>{p.measurement?.approach}</p>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>{p.measurement?.eligibility}</p>
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(p.measurement?.evidence_levels || []).map((e) => <span key={e} className="sr-badge sr-badge-gray">{e}</span>)}
          </div></div>
      </div>

      {p.radio_recommendation && <Block title="Radio recommendation"><p style={{ fontSize: 14 }}>{p.radio_recommendation}</p></Block>}

      {(p.required_approvals?.length > 0 || p.missing_items?.length > 0) && (
        <Block title="Before launch">
          {p.required_approvals?.length > 0 && <p style={{ fontSize: 14 }}><strong>Approvals needed:</strong> {p.required_approvals.join(", ")}</p>}
          {p.missing_items?.length > 0 && <p style={{ fontSize: 14, marginTop: 6 }}><strong>Missing:</strong> {p.missing_items.join(", ")}</p>}
        </Block>
      )}

      <div className="sr-card" style={{ marginTop: 18, display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <label className="sr-label">Revise monthly budget & regenerate</label>
          <input className="sr-input" type="number" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="plan-budget-input" />
        </div>
        <button className="sr-btn sr-btn-ghost" onClick={revise} disabled={busy} data-testid="plan-revise-btn"><RefreshCw size={15} /> Revise plan</button>
      </div>
      {createModal}
    </div>
  );
}
